import EngineRoomCanvas from '@/components/marketing3/EngineRoomCanvas'
import LedgerConsole3 from '@/components/marketing3/widgets/LedgerConsole3'
import { cn } from '@/lib/utils'

/**
 * Hero3 — the dark cinematic opening room ("the engine at night"). One of the
 * page's two islands of night. Layout + motion grammar carried from Landing2's
 * Hero: sequenced rise/wipe entrances, an H1 split across two lines with the second
 * line lit teal, atmosphere from the CSS hero-glow + blueprint grid, and the
 * shared ember canvas as a decorative layer (it self-skips on small screens and
 * under reduced motion; the CSS glow carries the still).
 *
 * Every visible string is verbatim from the copy doc's §Hero; the brand only ever
 * arrives via the `brand` prop. The dark room ends in a crisp cut: a single teal
 * hairline "horizon" marks its bottom edge, and overflow-hidden keeps the glow/ember
 * atmosphere contained so nothing bleeds past. The light page simply starts below.
 */

const container = 'mx-auto max-w-6xl px-4 sm:px-6'

export default function Hero3({ brand }: { brand: string }) {
  return (
    <section className="mkt3-dark relative overflow-hidden" aria-labelledby="hero-heading">
      {/* Decorative atmosphere: teal light source + blueprint grid + drifting embers.
          All aria-hidden and non-interactive. The canvas renders nothing on small
          screens / reduced motion, leaving the CSS glow to carry the composition. */}
      <div className="mkt3-hero-glow pointer-events-none absolute inset-0" aria-hidden="true" />
      <div className="mkt3-grid pointer-events-none absolute inset-0" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <EngineRoomCanvas className="absolute inset-0" />
      </div>

      <div
        className={cn(
          container,
          'relative grid min-h-[calc(100svh-4rem)] items-center gap-12 py-16 sm:py-20 lg:grid-cols-[1.05fr_1fr] lg:gap-16'
        )}
      >
        <div>
          <p className="mkt3-eyebrow mkt3-rise" style={{ animationDelay: '80ms' }}>
            Explainable revenue management for independent hotels
          </p>
          <h1 id="hero-heading" className="mkt3-display mkt3-h1 mt-6">
            <span className="mkt3-wipe" style={{ animationDelay: '220ms' }}>
              Your hotel sleeps.
            </span>
            <span className="mkt3-wipe mkt3-hot" style={{ animationDelay: '480ms' }}>
              The engine doesn&rsquo;t.
            </span>
          </h1>
          <p className="mkt3-prose mkt3-rise mt-7" style={{ animationDelay: '760ms' }}>
            {brand} works your calendar around the clock — and shows its working. Every price your hotel charges is
            built by a deterministic engine that writes down every step and accounts for every pound. You set the
            rules; you keep the wheel.
          </p>
          <div className="mkt3-rise mt-9 flex flex-wrap items-center gap-5" style={{ animationDelay: '940ms' }}>
            <a
              href="#demo"
              className="mkt3-cta-dark inline-flex h-12 items-center rounded-lg bg-teal-500 px-7 text-[15px] font-semibold text-slate-950 hover:bg-teal-400"
            >
              Book a demo
            </a>
            <a
              href="#how-it-works"
              className="inline-flex h-12 items-center text-[15px] font-medium text-slate-400 transition-colors hover:text-teal-300"
            >
              See how it works <span aria-hidden="true" className="ml-1.5">↓</span>
            </a>
          </div>
          <p className="mkt3-rise mt-5 text-sm text-slate-500" style={{ animationDelay: '1060ms' }}>
            No sales team, no obligation.
          </p>
        </div>

        <div className="mkt3-rise lg:pl-2" style={{ animationDelay: '620ms' }}>
          <LedgerConsole3 />
        </div>
      </div>

      {/* Horizon: a crisp teal hairline marks the dark room's bottom edge; the light
          page starts immediately below with no gradient fade. */}
      <div className="mkt3-horizon pointer-events-none absolute inset-x-0 bottom-0" aria-hidden="true" />
    </section>
  )
}
